import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileCTA from '@/components/MobileCTA';
import SEO from '@/components/SEO';

const PrivacyPage = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Privacy Policy',
      lastUpdated: 'Last Updated: January 2024',
      sections: [
        {
          title: 'Information We Collect',
          content: 'We collect information you provide directly to us, such as your name, email address, phone number, and delivery address when you request a quote or contact us for services.'
        },
        {
          title: 'How We Use Your Information',
          content: 'We use the information we collect to process your orders, communicate with you about our services, send you quotes and invoices, and improve our customer service.'
        },
        {
          title: 'Information Sharing',
          content: 'We do not sell, trade, or otherwise transfer your personal information to third parties. This does not include trusted third parties who assist us in operating our website or conducting our business, as long as those parties agree to keep this information confidential.'
        },
        {
          title: 'Data Security',
          content: 'We implement appropriate security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.'
        },
        {
          title: 'Your Rights',
          content: 'You have the right to access, correct, or delete your personal information. To exercise these rights, please contact us at thegrassdepotllc@gmail.com or call (936) 320-0763.'
        },
        {
          title: 'Contact Us',
          content: 'If you have any questions about this Privacy Policy, please contact us at:\nThe Grass Depot LLC\n6920 FM 1488 RD, Magnolia, TX 77354\nPhone: (936) 320-0763\nEmail: thegrassdepotllc@gmail.com'
        }
      ]
    },
    es: {
      title: 'Política de Privacidad',
      lastUpdated: 'Última Actualización: Enero 2024',
      sections: [
        {
          title: 'Información que Recopilamos',
          content: 'Recopilamos la información que nos proporciona directamente, como su nombre, dirección de correo electrónico, número de teléfono y dirección de entrega cuando solicita una cotización o nos contacta para servicios.'
        },
        {
          title: 'Cómo Usamos Su Información',
          content: 'Utilizamos la información que recopilamos para procesar sus pedidos, comunicarnos con usted sobre nuestros servicios, enviarle cotizaciones y facturas, y mejorar nuestro servicio al cliente.'
        },
        {
          title: 'Compartir Información',
          content: 'No vendemos, intercambiamos ni transferimos su información personal a terceros. Esto no incluye a terceros de confianza que nos ayudan a operar nuestro sitio web o realizar nuestro negocio, siempre que dichas partes acepten mantener esta información confidencial.'
        },
        {
          title: 'Seguridad de Datos',
          content: 'Implementamos medidas de seguridad apropiadas para proteger su información personal. Sin embargo, ningún método de transmisión por Internet es 100% seguro y no podemos garantizar seguridad absoluta.'
        },
        {
          title: 'Sus Derechos',
          content: 'Usted tiene derecho a acceder, corregir o eliminar su información personal. Para ejercer estos derechos, contáctenos en thegrassdepotllc@gmail.com o llame al (936) 320-0763.'
        },
        {
          title: 'Contáctenos',
          content: 'Si tiene alguna pregunta sobre esta Política de Privacidad, contáctenos en:\nThe Grass Depot LLC\n6920 FM 1488 RD, Magnolia, TX 77354\nTeléfono: (936) 320-0763\nEmail: thegrassdepotllc@gmail.com'
        }
      ]
    }
  };

  const currentContent = content[language as keyof typeof content];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title={currentContent.title}
        description={language === 'en' 
          ? 'Privacy Policy for The Grass Depot LLC - Learn how we protect and handle your personal information.'
          : 'Política de Privacidad de The Grass Depot LLC - Aprenda cómo protegemos y manejamos su información personal.'}
        canonical={`/${language}/${language === 'en' ? 'privacy' : 'privacidad'}`}
      />
      <Header />
      
      <main className="flex-grow bg-background">
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            {currentContent.title}
          </h1>
          <p className="text-muted-foreground mb-12">{currentContent.lastUpdated}</p>

          <div className="space-y-8">
            {currentContent.sections.map((section, index) => (
              <section key={index} className="bg-card p-6 rounded-lg border border-border shadow-sm">
                <h2 className="text-2xl font-semibold text-primary mb-4">
                  {section.title}
                </h2>
                <p className="text-foreground whitespace-pre-line leading-relaxed">
                  {section.content}
                </p>
              </section>
            ))}
          </div>
        </div>
      </main>

      <Footer />
      <MobileCTA />
    </div>
  );
};

export default PrivacyPage;
